import * as I from 'immutable';
import {Row} from 'react-table';

import {ApiFeature} from 'app/modules/Remote/Feature';

import {PropertyTableRow} from './PropertyTableTypes';

/**
 * Filter a list of rows to those that are (1) at a depth of 0, meaning they
 * represent an entire property, and (2) are fully or partially (e.g., only some
 * sub-rows) selected.
 */
export function getSelectedPropertyRows(rows: Row<PropertyTableRow>[]) {
  return rows.filter((row) => row.depth === 0 && (row.isSelected || row.isSomeSelected));
}

export function getSelectedFeatures(rows: Row<PropertyTableRow>[]): I.ImmutableOf<ApiFeature[]> {
  const selectedPropertyRows = getSelectedPropertyRows(rows);
  const selectedFeatures = selectedPropertyRows.reduce((accum, row) => {
    getSelectedPropertyFlatRows(row).forEach((flatRow) => {
      const feature = flatRow.original.features.first();
      if (feature) {
        accum.push(feature);
      }
    });
    return accum;
  }, [] as I.ImmutableOf<ApiFeature>[]);

  return I.List(selectedFeatures) as unknown as I.ImmutableOf<ApiFeature[]>;
}
/**
 * Returns true if the provided row is an expandable parent row for which only
 * some sub-rows are selected. Corrects for a react-table bug in which
 * `row.isSomeSelected` is inconsistently set by falling back to a manual check
 * of sub-row selection states.
 */
export function getIsSomeSelected(row: Row<PropertyTableRow>) {
  if (!row.subRows?.length) {
    return false;
  }

  const selectedSubRows = row.subRows.filter((subRow) => subRow.isSelected === true);

  return (
    row.isSomeSelected ||
    (selectedSubRows.length > 0 && selectedSubRows.length < row.subRows.length)
  );
}

/**
 * Given a row, return a flat list of rows. For single-location properties, this
 * is an array containing the original row. For multi-location properties, this
 * is an array of selected sub-rows.
 */
export function getSelectedPropertyFlatRows(row: Row<PropertyTableRow>) {
  // react-table sets the `row.isSomeSelected` property inconsistently, so we
  // use a wrapper function to attempt to identify indeterminate parent rows.
  const isSomeSelected = getIsSomeSelected(row);

  const selectedSubRows = row.subRows.filter((subRow) => subRow.isSelected === true);

  return isSomeSelected
    ? selectedSubRows
    : row.subRows?.length && row.isSelected
      ? row.subRows
      : [row];
}

/**
 * Returns true if the provided row is a sub-row of another.
 */
export function getIsSubRow(row: Row<PropertyTableRow>) {
  return row.depth > 0;
}
